import { environmentAtlas, crateBreakAnimation } from '../animation/Theme';
import { ExplosionAnimationController } from '../animation/ExplosionAnimationController';
import Phaser from 'phaser';
import { TILE, BOMB_EXPLODE_MS } from '../constants';
import type { ExplosionCell } from '../types';

// ─── Colour palette ──────────────────────────────────────────────────────────
const COLOR_CENTER      = 0xffffff; // bright white-yellow core
const COLOR_CENTER_ALT  = 0xffff00;
const COLOR_MID         = 0xff6600; // orange arms
const COLOR_TIP         = 0xff3300; // deeper orange-red tips
const COLOR_SMOKE       = 0x555555; // dark grey smoke particles

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Converts a grid column/row to the pixel centre of that cell. */
function cellToPixel(col: number, row: number): { x: number; y: number } {
  return {
    x: col * TILE + TILE / 2,
    y: row * TILE + TILE / 2,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

export class ExplosionSystem {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    // Render above the map layer but below UI.
    this.container.setDepth(10);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Instantiates all visual rectangles for one explosion event and kicks off
   * the tween-based animation.  Everything auto-destroys when done.
   */
  createExplosion(cells: ExplosionCell[]): void {
    ExplosionAnimationController.create(this.scene, this.container, cells);
    if (cells.some(cell => cell.type === 'center')) this.scene.cameras.main.shake(200, 0.008);
  }

  // -------------------------------------------------------------------------

  /**
   * Spawns debris rectangles that fly outward from a destroyed soft block.
   */
  createBlockBreakEffect(col: number, row: number, color: number): void {
    const { x, y } = cellToPixel(col, row);
    const debris = this.scene.add.sprite(x, y, environmentAtlas(), 'obstacles/crate-break/0').setOrigin(0.5, 0.7).setScale(0.48);
    this.container.add(debris);
    debris.play(crateBreakAnimation());
    this.scene.time.delayedCall(400, () => debris.destroy());
  }

  // -------------------------------------------------------------------------

  /**
   * Ring-expand + sparkle effect when a player collects a power-up.
   */
  createPowerUpCollectEffect(x: number, y: number, color: number): void {
    // Expanding ring.
    const ring = this.scene.add.circle(x, y, TILE * 0.3, color, 0);
    ring.setStrokeStyle(3, color, 1);
    ring.setDepth(12);
    this.container.add(ring);

    this.scene.tweens.add({
      targets: ring,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Sparkle particles (small rectangles).
    const sparkCount = 8;
    for (let i = 0; i < sparkCount; i++) {
      const spark = this.scene.add.rectangle(
        x,
        y,
        Phaser.Math.Between(4, 8),
        Phaser.Math.Between(4, 8),
        color,
        1,
      );
      spark.setDepth(12);
      this.container.add(spark);

      const angle = (i / sparkCount) * Math.PI * 2;
      const dist = Phaser.Math.Between(30, 55);

      this.scene.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        scaleX: 0,
        scaleY: 0,
        duration: 600,
        ease: 'Quad.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
  }

  // -------------------------------------------------------------------------

  /**
   * No-op stub – Phaser tweens drive all animations.
   * Kept for interface compatibility with systems that call update().
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  update(_delta: number): void {
    // Intentionally empty – all motion is driven by scene tweens.
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  /**
   * Creates dark smoke circles that drift upward from the explosion centre.
   */
  private createSmokeParticles(x: number, y: number): void {
    const count = Phaser.Math.Between(5, 10);

    for (let i = 0; i < count; i++) {
      const radius = Phaser.Math.Between(6, 14);
      const offsetX = Phaser.Math.Between(-TILE * 0.3, TILE * 0.3);
      const smoke = this.scene.add.circle(x + offsetX, y, radius, COLOR_SMOKE, 0.7);
      smoke.setDepth(13);
      this.container.add(smoke);

      const riseDistance = Phaser.Math.Between(40, 80);
      const delay = Phaser.Math.Between(0, 150);
      const duration = Phaser.Math.Between(500, BOMB_EXPLODE_MS + 200);

      this.scene.tweens.add({
        targets: smoke,
        y: smoke.y - riseDistance,
        alpha: 0,
        scaleX: 1.8,
        scaleY: 1.8,
        delay,
        duration,
        ease: 'Sine.easeOut',
        onComplete: () => smoke.destroy(),
      });
    }
  }

  // -------------------------------------------------------------------------
  // Teardown
  // -------------------------------------------------------------------------

  /**
   * Destroys the container and all child objects.
   * Call on scene shutdown.
   */
  destroy(): void {
    this.container.destroy(true);
  }
}
